/**
 * Custom hooks per Document Types
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api-client";
import { toast } from "@/lib/toast-service";
import type { DocumentTypeCreate, DocumentTypeListFilters, DocumentTypeResponse, DocumentTypeUpdate } from "@/types/api/document-types";

const DOCUMENT_TYPES_QUERY_KEYS = {
    all: ["document-types"] as const,
    lists: () => [...DOCUMENT_TYPES_QUERY_KEYS.all, "list"] as const,
    list: (filters?: DocumentTypeListFilters) => [...DOCUMENT_TYPES_QUERY_KEYS.lists(), filters] as const,
    details: () => [...DOCUMENT_TYPES_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...DOCUMENT_TYPES_QUERY_KEYS.details(), id] as const,
    phases: () => [...DOCUMENT_TYPES_QUERY_KEYS.all, "phases"] as const,
    categories: () => [...DOCUMENT_TYPES_QUERY_KEYS.all, "categories"] as const,
};

/**
 * Hook per lista document types
 */
export function useDocumentTypes(filters?: DocumentTypeListFilters) {
    return useQuery({
        queryKey: DOCUMENT_TYPES_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<DocumentTypeResponse[]> => {
            return await apiGet<DocumentTypeResponse[]>("/document-types", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        staleTime: 10 * 60 * 1000, // 10 minuti - i document types cambiano raramente
    });
}

/**
 * Hook per singolo document type
 */
export function useDocumentType(documentTypeId: string) {
    return useQuery({
        queryKey: DOCUMENT_TYPES_QUERY_KEYS.detail(documentTypeId),
        queryFn: async (): Promise<DocumentTypeResponse> => {
            return await apiGet<DocumentTypeResponse>(`/document-types/${documentTypeId}`);
        },
        enabled: !!documentTypeId,
        staleTime: 10 * 60 * 1000,
    });
}

/**
 * Hook per creare document type
 */
export function useCreateDocumentType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: DocumentTypeCreate): Promise<DocumentTypeResponse> => {
            return await apiPost<DocumentTypeResponse>("/document-types", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEYS.lists() });
            toast.success("Document type creato con successo");
        },
    });
}

/**
 * Hook per aggiornare document type
 */
export function useUpdateDocumentType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ documentTypeId, data }: { documentTypeId: string; data: DocumentTypeUpdate }): Promise<DocumentTypeResponse> => {
            return await apiPut<DocumentTypeResponse>(`/document-types/${documentTypeId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEYS.detail(variables.documentTypeId) });
            toast.success("Document type aggiornato con successo");
        },
    });
}

/**
 * Hook per lista fasi (phases) dei document types
 */
export function useDocumentTypePhases() {
    return useQuery({
        queryKey: DOCUMENT_TYPES_QUERY_KEYS.phases(),
        queryFn: async (): Promise<string[]> => {
            return await apiGet<string[]>("/document-types/phases");
        },
        staleTime: 10 * 60 * 1000, // 10 minuti
    });
}

/**
 * Hook per lista categorie dei document types
 */
export function useDocumentTypeCategories() {
    return useQuery({
        queryKey: DOCUMENT_TYPES_QUERY_KEYS.categories(),
        queryFn: async (): Promise<string[]> => {
            return await apiGet<string[]>("/document-types/categories");
        },
        staleTime: 10 * 60 * 1000, // 10 minuti
    });
}

/**
 * Hook per eliminare document type
 */
export function useDeleteDocumentType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (documentTypeId: string): Promise<void> => {
            return await apiDelete<void>(`/document-types/${documentTypeId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEYS.lists() });
            toast.success("Document type eliminato con successo");
        },
    });
}
